import { useState, useEffect, useRef, useMemo } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export default function AddToGroupModal({ isOpen, onClose, onConfirm, orgTree = [], currentGroupIds = [] }) {
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedCampusId, setSelectedCampusId] = useState('');
  const [selectedMinistryId, setSelectedMinistryId] = useState('');
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const backdropRef = useRef(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      setSelectedOrgId('');
      setSelectedCampusId('');
      setSelectedMinistryId('');
      setSelectedGroupIds([]);
    }
  }, [isOpen]);

  // Reset downstream selections when parent changes
  useEffect(() => {
    setSelectedCampusId('');
    setSelectedMinistryId('');
    setSelectedGroupIds([]);
  }, [selectedOrgId]);

  useEffect(() => {
    setSelectedMinistryId('');
    setSelectedGroupIds([]);
  }, [selectedCampusId]);

  useEffect(() => {
    setSelectedGroupIds([]);
  }, [selectedMinistryId]);

  // Build derived options (must be before early return to satisfy Rules of Hooks)
  const selectedOrg = orgTree.find((o) => o.id === selectedOrgId);
  const campuses = selectedOrg?.campuses || [];
  const selectedCampus = campuses.find((c) => c.id === selectedCampusId);

  // Collect ministries from selected campus, or all campuses if none selected
  const ministries = selectedCampus
    ? selectedCampus.ministries || []
    : campuses.flatMap((c) => c.ministries || []);

  const selectedMinistry = ministries.find((m) => m.id === selectedMinistryId);

  // Collect groups based on filters
  const allGroups = useMemo(() => {
    const groups = [];
    if (!selectedOrg) return groups;

    for (const campus of selectedOrg.campuses || []) {
      if (selectedCampusId && campus.id !== selectedCampusId) continue;

      // Groups directly under campus (no ministry)
      for (const group of campus.groups || []) {
        if (!currentGroupIds.includes(group.id)) {
          groups.push({ ...group, orgName: selectedOrg.name, campusName: campus.name, ministryName: null });
        }
      }

      for (const ministry of campus.ministries || []) {
        if (selectedMinistryId && ministry.id !== selectedMinistryId) continue;
        for (const group of ministry.groups || []) {
          if (!currentGroupIds.includes(group.id)) {
            groups.push({ ...group, orgName: selectedOrg.name, campusName: campus.name, ministryName: ministry.name });
          }
        }
      }
    }

    // Groups directly under org (no campus)
    for (const group of selectedOrg.groups || []) {
      if (!currentGroupIds.includes(group.id)) {
        groups.push({ ...group, orgName: selectedOrg.name, campusName: null, ministryName: null });
      }
    }

    return groups;
  }, [selectedOrg, selectedCampusId, selectedMinistryId, currentGroupIds]);

  if (!isOpen) return null;

  const toggleGroup = (groupId) => {
    setSelectedGroupIds((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    );
  };

  const handleAdd = async () => {
    if (selectedGroupIds.length === 0) return;
    setLoading(true);
    try {
      await onConfirm(selectedGroupIds);
      onClose();
    } catch (err) {
      // Error handled by parent
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === backdropRef.current) onClose(); }}
    >
      <div className="absolute inset-0 bg-deep-navy/60" />

      <div className="relative bg-white rounded-card shadow-card p-6 max-w-2xl w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-deep-navy">Add to Groups</h3>
          <button onClick={onClose} className="min-h-touch min-w-touch flex items-center justify-center rounded-lg hover:bg-gray-100 transition-all">
            <XMarkIcon className="w-5 h-5 text-storm-blue" />
          </button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          {/* Org dropdown */}
          <div>
            <label className="block text-sm font-medium text-storm-blue mb-1">Organization</label>
            <select
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              className="w-full px-3 py-2.5 min-h-touch rounded-xl border-2 border-gray-200 focus:border-storm-blue focus:outline-none transition-all text-[16px] bg-white"
            >
              <option value="">All Organizations</option>
              {orgTree.map((org) => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>

          {/* Campus dropdown */}
          <div>
            <label className="block text-sm font-medium text-storm-blue mb-1">Campus</label>
            <select
              value={selectedCampusId}
              onChange={(e) => setSelectedCampusId(e.target.value)}
              disabled={!selectedOrgId}
              className="w-full px-3 py-2.5 min-h-touch rounded-xl border-2 border-gray-200 focus:border-storm-blue focus:outline-none transition-all text-[16px] bg-white disabled:bg-gray-100 disabled:text-gray-400"
            >
              <option value="">All Campuses</option>
              {campuses.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Ministry dropdown */}
          <div>
            <label className="block text-sm font-medium text-storm-blue mb-1">Ministry</label>
            <select
              value={selectedMinistryId}
              onChange={(e) => setSelectedMinistryId(e.target.value)}
              disabled={!selectedOrgId}
              className="w-full px-3 py-2.5 min-h-touch rounded-xl border-2 border-gray-200 focus:border-storm-blue focus:outline-none transition-all text-[16px] bg-white disabled:bg-gray-100 disabled:text-gray-400"
            >
              <option value="">All Ministries</option>
              {ministries.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Group checkbox list */}
        <div className="flex-1 overflow-y-auto min-h-0 border-2 border-gray-200 rounded-xl mb-4">
          {!selectedOrgId ? (
            <p className="text-center text-sky-blue py-8">Select an organization to see available groups</p>
          ) : allGroups.length === 0 ? (
            <p className="text-center text-sky-blue py-8">No available groups found (user may already be in all groups)</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {allGroups.map((group) => (
                <label
                  key={group.id}
                  className="flex items-center gap-3 px-4 py-3 min-h-touch hover:bg-gray-50 cursor-pointer transition-all"
                >
                  <input
                    type="checkbox"
                    checked={selectedGroupIds.includes(group.id)}
                    onChange={() => toggleGroup(group.id)}
                    className="w-5 h-5 rounded border-gray-300 text-storm-blue focus:ring-storm-blue"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-deep-navy truncate">{group.name}</p>
                    <p className="text-xs text-sky-blue truncate">
                      {[group.campusName, group.ministryName].filter(Boolean).join(' / ') || 'No campus/ministry'}
                    </p>
                  </div>
                  {group.member_count != null && (
                    <span className="text-xs font-medium text-sky-blue bg-gray-100 px-2 py-1 rounded-full">
                      {group.member_count} members
                    </span>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="min-h-touch px-5 py-2.5 rounded-button font-semibold text-storm-blue bg-gray-100 hover:bg-gray-200 transition-all active:scale-[0.98]"
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            disabled={selectedGroupIds.length === 0 || loading}
            className={`min-h-touch px-5 py-2.5 rounded-button font-semibold text-white transition-all active:scale-[0.98] ${
              selectedGroupIds.length === 0 || loading
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-gradient-button shadow-button'
            }`}
          >
            {loading ? 'Adding...' : `Add Selected (${selectedGroupIds.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}
